import React, { useState, useEffect } from 'react';
import axios from 'axios';
import StyledSelect from '../StyledSelect';
import { API } from '../../lib/api';

const UserCreationModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'vertriebler',
    team_id: '',
    managed_by: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [teamleiters, setTeamleiters] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchTeamleiters();
    }
  }, [isOpen]);

  const fetchTeamleiters = async () => {
    try {
      const response = await axios.get(`${API}/admin/teamleiters`);
      setTeamleiters(response.data);
    } catch (error) {
      console.error('Error fetching teamleiters:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API}/admin/create-user`, formData);
      if (response.status === 200) {
        const result = response.data;
        setSuccess(result.message);
        setFormData({ 
          name: '', 
          email: '', 
          role: 'vertriebler',
          team_id: '',
          managed_by: ''
        });
        
        // Show success message briefly then close
        setTimeout(() => {
          onSuccess();
          onClose();
          setSuccess('');
        }, 2000);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Neuen Benutzer erstellen</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Info-Box statt Passwort-Feld */}
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium">Passwort wird vom Benutzer erstellt</p>
                <p className="text-xs mt-1">Der Benutzer erhält eine E-Mail mit einem sicheren Link zur Passwort-Erstellung.</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rolle</label>
            <StyledSelect
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
            >
              <option value="vertriebler">Vertriebler</option>
              <option value="teamleiter">Teamleiter</option>
              <option value="vertriebsleiter">Vertriebsleiter</option>
              <option value="backoffice">Backoffice</option>
              <option value="admin">Admin</option>
            </StyledSelect>
          </div>

          {/* Team Assignment for Vertriebler (required) and Teamleiter (optional) */}
          {(formData.role === 'vertriebler' || formData.role === 'teamleiter') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teamleiter zuweisen
                {formData.role === 'vertriebler' && <span className="text-red-500 ml-1">*</span>}
                {formData.role === 'teamleiter' && <span className="text-gray-400 ml-1 font-normal">(optional)</span>}
              </label>
              <StyledSelect
                value={formData.managed_by}
                onChange={(e) => setFormData({ ...formData, managed_by: e.target.value })}
              >
                <option value="">Kein Teamleiter zugewiesen</option>
                {teamleiters.map((teamleiter) => (
                  <option key={teamleiter.id} value={teamleiter.id}>
                    {teamleiter.name} ({teamleiter.email})
                  </option>
                ))}
              </StyledSelect>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {loading ? 'Erstellen...' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserCreationModal;
